import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";
import { InvestmentTypeEnum, useSearchSymbolV2 } from "@bn/api/apiv1";
import { useQuotesForSymbols } from "../hooks/useQuotesForSymbols";
import { SymbolSearchInput } from "./SymbolSearchInput";
import { SearchExamples } from "./SearchExamples";

const Container = styled.div`
  width: 100%;
`;

export interface Props {
  search?: string;
  noManual?: boolean;
  noExample?: boolean;
  defaultSearchValue?: string;
  hasQuote?: boolean;
  onSelectSymbol: (symbol: SymbolWithQuote) => void;
  type?: InvestmentTypeEnum;
  depotId?: number;
  shouldShowQuote?: boolean;
}

export const SymbolSearch = ({
  search,
  defaultSearchValue,
  hasQuote,
  onSelectSymbol,
  shouldShowQuote,
  noManual,
  noExample,
  type,
  depotId,
}: Props) => {
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined>(
    search ? search : defaultSearchValue
  );

  const handleSelect = (symbol: SymbolWithQuote) => {
    onSelectSymbol(symbol);
    setIsSearchOpen(false);
  };

  const handleChange = (value: string) => {
    setSearchValue(value);
    setIsSearchOpen(true);
  };

  const openManualWizard = () => {
    const params = [
      ["type", type],
      ["depotId", depotId !== undefined ? String(depotId) : undefined],
    ].filter(([_, value]) => Boolean(value)) as [string, string][];
    const string = new URLSearchParams(params).toString();

    navigate(`/manual-transaction-wizard${string.length ? `?${string}` : ""}`);
  };

  const { data, isFetching, isPending } = useSearchSymbolV2({
    search: searchValue || defaultSearchValue,
    perAssetType: 10,
  });

  const symbolsWithQuotes = useQuotesForSymbols({
    symbols: data?.symbols || [],
  });

  useEffect(() => {
    setIsSearchOpen(true);
  }, [data]);

  useEffect(() => {
    if (!hasQuote && Boolean(symbolsWithQuotes.length)) {
      onSelectSymbol(symbolsWithQuotes[0]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolsWithQuotes]);

  return (
    <Container>
      <SymbolSearchInput
        search={searchValue || ""}
        handleChange={handleChange}
        symbols={symbolsWithQuotes || []}
        showLoadingSkeleton={isFetching}
        showNoResults={!isFetching && !isPending && !data?.symbols?.length}
        onSelectSymbol={handleSelect}
        isSearchOpen={isSearchOpen}
        shouldShowQuote={shouldShowQuote}
        onFocus={() => {
          setIsSearchOpen(true);
        }}
      />

      {(isPending || (!isFetching && !data?.symbols?.length)) && !noExample && (
        <SearchExamples
          handleManualInvestmentClick={openManualWizard}
          noManual={noManual}
        />
      )}
    </Container>
  );
};
